export const voiceCallBlockDocumentationMarkdown = `
### What does this block do?

This block creates automated outbound phone calls using AI assistants that can conduct natural conversations with patients. The assistant can verify identity, provide appointment information, collect clinical data, handle rescheduling, and more—all while responding intelligently to patient queries and following healthcare compliance requirements.

### When to use it?

Use this block when you need to engage with patients through voice communication for scenarios such as:
  - Appointment confirmations and reminders
  - Appointment scheduling
  - Pre-visit preparation and intake questionnaires
  - Medication adherence checks and follow-ups
  - Post-discharge check-ins
  - Care gap closures
  - Multilingual patient support
  - Wellness check-ins for chronic condition management

## How to Configure This Block

### **1. Determine the phone number to call**
Enter the phone number of the person being called. (Required)
- **Production Examples:**
~~~
{{patient.data.phoneNumber}}
{{patient.data.cellPhoneNumber}}
{{patient.data.homePhoneNumber}}
{{patient.data.workPhoneNumber}}
~~~
- **Test Example:** Enter your own phone number to test the flow.

### **2. Provide instructions to the assistant on its scope via prompts**
**Enter the prompt** with clear and detailed descriptions telling the assistant the task, context and desired outcome. See best practices section for more details on how to write effective prompts.
- **Example Prompt Structure:**
~~~
##Identity
You are a practice coordinator calling the patient to help them prepare for an upcoming PCP visit. Be polite, calm, patient, and professional.

If the patient tells you that they need to reschedule respond with, "okay I can help you take care of that, but first let's verify your identity". Verify the patient's identity first, then proceed to reschedule the appointment.

##Style
After all questions have been answered, ask if there is anything else you can help them with. Thank them for their time and say you looking forward to seeing the patient at the end of the call.

##Guardrails
Do not give them any information unless they explicitly ask for it. Give answers one at a time from the information in {{Practice_details.data}}.

##Tasks and Goals
Verification:
Please verify the patient's identity using the authenticateKnownPatient tool before asking them any questions. Do not use the patient's name until they have been verified.
...
~~~

**Write the start of the conversation** so the assistant knows what to say when the call is started:

\`Hi, this is Sam, an intelligent virtual assistant calling on behalf of Notable Health System. I'm here to help you prepare for your upcoming appointment. Do you have a few minutes to answer a couple of questions?\`

**Select who should speak first:**

- 'Assistant Speaks First' to have the assistant speak first.
- 'Assistant Waits For User' to have the assistant wait for the user to speak first.
- 'Assistant Speaks First With Model Generated Message' to have the assistant speak first with a message generated by the model based on the conversation state (this overrides the First Message).

**Enter delay duration before Assistant starts speaking**

The range is from 0 to 5. The default is 2.0 seconds, but you can increase it if the assistant is speaking too soon, or decrease it if there's too much delay. For example, tech support calls will set to more than 1.0 seconds to give customers time to complete their thoughts, even if they have some pauses in between.

**Add a message for the end of the call**
This is what the assistant will say at the end of the call. This is optional.

\`Thanks for your time. I hope you have a great day.\`

**Add the ability for the assistant to end the call from its side**

  Enabling this will allow the assistant to end the call and not wait for the user to end the call.

**Add voice mail message**

If the assistant cannot reach the callee, write a script the assistant can use to leave a voice mail:

\`Hi, this is Sam, an intelligent virtual assistant calling on behalf of Notable Health System. I was calling to ask you a couple of questions to help you and your healthcare provider prepare for your upcoming appointment. It's not a problem that we didn't speak. Your provider will discuss these questions during your scheduled visit. Thank you and have a great day.\`

**Add a timeout failure time duration**

Tell the assistant the flow has failed if no data has been received from the callee and a voice mail message has not been delivered. The range is from 10 seconds to 60 minutes.

**Specify the idle messages**

These are messages the assistant will speak when the user hasn't responded:  
- Are you still there?
- Is there anything else you need help with?
- Feel free to ask me any questions.
- How can I assist you further?
- Let me know if there is anything you need?
- I'm still here if you need assistance.
- I'm ready to help whenever you are.
- Is there something specific you're looking for?
- I'm here to help with any questions you have.

**Add the max number of Idle messages spoken.**

This determines the maximum number of times Idle Messages can be spoken during the call. The range is from 1-10, whole integers only.

**Set the value for idle timeout**

This is the timeout in seconds before a message from Idle Messages is spoken. The clock starts when the assistant finishes speaking and remains active until the user speaks. The range is from 5 seconds to 60 seconds and decimals are allowed.

**Set the value for the amount of words the callee has to speak before the assistant stops talking**

This is the amount of words the callee has to speak before the assistant stops talking. The range is from 1 to 10, whole integers only.

**Set the value for the amount of time the callee has to speak before the assistant stops talking**

This is the amount of time (in seconds) the callee has to speak before the assistant stops talking. The range is from 0 to 0.5 seconds.

**Set the number of seconds the assistant will wait before speaking again**

This is the number of seconds the assistant will wait before speaking again. The range is from 0 to 10 seconds.

**Set the timeout to failure**

This is the amount of time before the voice call times out. If the voice call times out, the block will fail.

**Configure Pre-Assistant Message**

Enable a pre-assistant message that will be spoken to the caller before connecting them to the AI assistant. This message itself is not transcribed or recorded.

- **Enable pre-assistant message**: Toggle to enable or disable the pre-assistant message feature.
- **Pre-assistant message**: The message that will be spoken to the caller before connecting them to the AI assistant. Basic SSML tags are allowed.

**Examples:**
- '<break time="5s"/>Hello, this is from [Organization Name]. This call may be recorded for quality assurance and training purposes. If you do not consent to being recorded, please hang up now. By continuing with this call, you agree to recording.<break time="1.5s"/>'

** Note:**
- Recommend to configure the AI Assistant to speak first when using this pre-assistant message to avoid silence after the pre-assistant message is spoken. 

**Configure Valid Hours**

Restrict voice calls to specific hours of the day to ensure calls are made during appropriate times for your patients.
The block will fail if the call is made outside of the valid hours.

- **Enable valid hours restriction**: Toggle to enable or disable the time restriction feature.
- **Start hour**: The hour (0-23) when voice calls are allowed to begin. Uses 24-hour format in UTC timezone.
- **End hour**: The hour (0-23) when voice calls are allowed to end. Must be equal to or greater than the start hour. Uses 24-hour format in UTC timezone. The full end hour is inclusive.

**Examples:**
- For business hours (9 AM to 5 PM UTC): Start hour = 9, End hour = 17
- For extended hours (8 AM to 8 PM UTC ): Start hour = 8, End hour = 20
- For overnight hours (10 PM to 6 AM UTC): Start hour = 22, End hour = 6
- For 24/7 availability: Disable the valid hours restriction or set the start and end hours to 0 and 23 respectively. 

**Note:** All times are in UTC timezone. Consider your patient population's local time zones when setting these values.


### **3. Fine-Tune Voice Settings**

**Note:** Configuration options vary by voice provider and model type:
- **OpenAI Voice Provider:** Language selection, voice similarity, voice speed, voice stability, emotion recognition, background noise filtering, background sound, and endpointing are not available.
- **OpenAI Realtime Models:** In addition to the OpenAI provider limitations above, idle messages and interruption handling settings are also not available.

- **Select the LLM model to use for the call**
  Choose from available language models, including standard models and OpenAI Realtime models.
  
  **OpenAI Realtime Models (Beta):**
  OpenAI's Realtime models are currently in beta and **not recommended for production deployments at scale**. These models use speech-to-speech processing for ultra-low latency, which can improve conversational quality and handle pronunciation challenges better than text-to-speech alternatives. However, there is a known limitation with tool calling that may cause unexpected behavior.
  
  **Configuration Requirements for Realtime Models:**
  - Temperature must be set between 0.5-0.7 (defaults to 0.5)
  - Voice Provider must be set to OpenAI
  - Language is automatically set to English (multilingual not supported)
  
  **Important Resources:**
  - [OpenAI Realtime Prompting Guide](https://cookbook.openai.com/examples/realtime_prompting_guide)
  - [Vapi Realtime Best Practices](https://docs.vapi.ai/openai-realtime#prompting-best-practices)

- **Select the model temperature**
  This is the temperature of the model. The range is from 0.0 to 2.0 and allows decimals.
  - **Higher temperature settings:** Makes the model more creative and less focused on the context.
  - **Lower temperature settings:** Makes the model more deterministic and focused on the context.
  - **Note:** OpenAI Realtime models require temperature between 0.5-0.7 for optimal performance.

- **Select the Voice Provider**
  Choose the voice synthesis provider for the assistant's speech:
  - **11Labs (Default):** High-quality text-to-speech with extensive voice customization options and multilingual support.
  - **OpenAI:** Required for OpenAI Realtime models. Provides ultra-low latency speech-to-speech processing.
  - **Cartesia:** Alternative text-to-speech provider with additional voice options.
  
  **Note:** OpenAI voice provider is automatically selected and locked when using OpenAI Realtime models.

- **Select the language**
  Select the language the Assistant will use to speak to the callee. The language spoken by the Assistant will be this language, but the patient's preferences can override this. Choose "Multilingual" to enable automatic language detection and higher-quality voice generation for diverse patient populations, or select a specific language for optimized speed and performance.

- **Choose the voice persona for the Assistant**
  Select a voice that best represents your organization and matches the needs of your patient population. Available voices depend on your selected Voice Provider. Check out the best practices for guidance on configuring the voice persona.

| **Voice Persona** | **Accent**                | **Description**                                            | **Recommendation for Use Case**                     |
|--------------------|--------------------------|------------------------------------------------------------|------------------------------------------------------------------------------------------|
| Alice             | British                   | Calm and composed young millennial female                  | Medical appointment scheduling, gentle reminders                                         |
| Arabella          | American                  | Young, mature female voice with emotive tone               | General purpose, sensitive matters                                                       |
| Arnold            | American                  | Assertive, deep voice, authoritative                       | Urgent notifications, security matters                                                   |
| Bill              | American                  | Friendly, conversational middle-aged male voice            | General practice outreach, wellness checks                                               |
| Brian             | American                  | Professional, articulate male voice                        | Specialist referrals, technical explanations                                             |
| Carolina          | Spanish (Multilingual)    | Warm, professional female spanish voice.                   | Spanish speaking patients, medical appointment scheduling, multilingual conversations    |
| Charlie           | Australian (Multilingual) | Casual, friendly male voice with accent                    | General practice, community outreach, multilingual conversations                         |
| Dakota            | American                  | Clear, conversational female voice                         | General purpose, broad audience communications                                           |
| Daniel            | British                   | Refined, professional male voice with formal tone          | Executive health programs, VIP patient communications                                    |
| Dorothy           | British                   | Warm, mature female voice with compassionate tone          | Geriatric care, palliative services                                                      |
| Eduardo           | Spanish (Multilingual)    | Clear, articulate male spanish voice                       | Spanish speaking patients, detailed instructions, multilingual conversations             |
| Eric              | American (Multilingual)   | Clear, articulate male american voice                      | General practice outreach, wellness checks, multilingual conversations                   |
| Gabriela          | Spanish                   | Refined, friendly female spanish voice                     | Spanish speaking patients, general practice outreach, wellness checks                    |
| Javier            | Spanish                   | Professional, confident male spanish voice                 | Spanish speaking patients, administrative communications, insurance matters              |
| Jessa             | American                  | Natural, relatable female voice with a casual tone         | General practice outreach, wellness checks                                               |
| Jessica           | American (Multilingual)   | Friendly, energetic young adult female voice               | Pediatric appointment reminders, preventive care, multilingual conversations             |
| Josh              | American                  | Casual, approachable young adult male voice                | Student health services, preventive wellness                                             |
| Kelli             | American - Southern       | Warm, hospitable female voice with gentle Southern accent  | Rural healthcare, community outreach                                                     |
| Liam              | American                  | Clear, neutral male voice                                  | General purpose, broad audience communications                                           |
| Maribeth          | American - Southern       | Warm, mature female voice with subtle Southern charm       | Patient education, chronic care management                                               |
| Matthew           | British                   | Clear, authoritative male voice with precise articulation  | Specialist consultations, detailed instructions                                          |
| Nicole            | American                  | Professional, confident female voice                       | Administrative communications, insurance matters                                         |
| Paul              | American                  | Calm, reassuring middle-aged male voice                    | Mental health communications, sensitive matters                                          |
| Rachel            | American                  | Clear, friendly female voice with professional tone        | Primary care communications, general updates                                             |
| Rhett             | American - Southern       | Conversational male voice with subtle Southern accent      | Community health initiatives, rural outreach                                             |
| Sarah             | American (Multilingual)   | Nurturing, compassionate female voice                      | Maternal care, pediatric services, multilingual conversations                            |
| Serena            | American                  | Articulate, professional female voice                      | Specialist appointment coordination, clinical trials                                     |


**OpenAI Voice Personas (Available when Voice Provider is set to OpenAI):**

| **Voice Persona** | **Accent**                | **Description**                                            | **Recommendation for Use Case**                     |
|--------------------|--------------------------|------------------------------------------------------------|------------------------------------------------------------------------------------------|
| Alloy             | American                  | Neutral, balanced voice suitable for general use           | General purpose communications, broad audience                                           |
| Echo              | American                  | Clear, articulate voice with professional tone             | Administrative communications, appointment reminders                                     |
| Shimmer           | American                  | Warm, friendly voice with approachable tone                | Patient education, wellness checks                                                       |
| Marin*            | American                  | Natural, conversational voice (Realtime-exclusive)         | Ultra-low latency calls with OpenAI Realtime models                                      |
| Cedar*            | American                  | Clear, professional voice (Realtime-exclusive)             | Ultra-low latency calls with OpenAI Realtime models                                      |

***Note:** Marin and Cedar are only available with OpenAI Realtime models. Alloy, Echo, and Shimmer work with all OpenAI models.

- **Adjust Voice Similarity**
Controls how closely the AI adheres to the original voice when attempting to replicate it. The range is from 0 to 1 and allows decimals.

  - **Higher similarity settings (0.8-1.0):** Makes the AI try to match the original voice more closely.
  - **Medium similarity settings (0.5-0.7):** Balanced approach that captures voice characteristics while smoothing imperfections.
  - **Lower similarity settings (0-0.4):** Focuses more on clarity than exact matching.

**Recommended starting point:** 0.75-0.80.

- **Adjust Voice Speed**
This is the speed of the voice. The range is from 0.7 to 1.2 and allows decimals.

  - **Higher speed settings:** Makes the voice faster.
  - **Lower speed settings:** Makes the voice slower.

**Add Emotion Recognition**

Extracts the emotional inflection of the user's statement. This emotional information is fed into the LLM, so it knows to behave differently if the user is angry, annoyed, or confused.

**Add Filter to Remove Background Noise**

Enable this to ensure the audio is cleaned when the user is talking.

**Configure Background Sound**

Select the background sound that will be played during the call. Choose between:
- **Off**: No background sound
- **Office**: Office ambient noise

**Enable Endpointing**

Determines when the callee is done speaking. Based on both the user's tone and what they're saying, the assistant decides how long to pause before hitting the LLM. This is critical to ensure the user isn't interrupted mid-thought while still providing sub-second response times when they're done speaking.

### **4. Give the Assistant Access to Tools**

Select the tools to use on the call and add the metadata needed to execute the tools. These are the tools available and what metadata is required for each:

- **Authenticate Known Patient**  
  Authenticates the patient by verifying their phone number, last name, and date of birth.  
  - Requires:
    - *ehrInformationId* is included in the metadata (included by default).
    - The phone number being called corresponds to the patient's phone number.
  - Tool Arguments (provided by the assistant during the call):
    - *lastName* is the last name of the patient to authenticate.
    - *dateOfBirth* is the date of birth of the patient to authenticate.

- **Search For Open Slots**  
  Searches for open appointment slots.
  - Requires:
    - *ehrInformationId* is included in the metadata (included by default).
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  - Tool Arguments (provided by the assistant during the call):
    - *practiceIds* is an array of practice IDs to search for slots at.
    - *providerUserIds* is an array of provider IDs as stored in Notable to search for slots with.
    - *appointmentTypes* is an array of appointment types to search for slots with.
    - *startTime* is the start time to search for slots with.
    - *endTime* is the end time to search for slots with.
  - Optional:
    - *schedulingConfig* can be included in the tools metadata to provide additional configuration for the search for open slots tool.
      - *schedulingConfig.practiceIds* is an array of practice IDs to search for slots at.
      - *schedulingConfig.providerUserIds* is an array of provider IDs as stored in Notable to search for slots with.
      - *schedulingConfig.appointmentTypes* is an array of appointment types to search for slots with.
      - *schedulingConfig.limit* is the maximum number of slots to search for during each tool call.
      - *schedulingConfig.searchForOpenSlotsMockEnabled* boolean to enable mock data for the search for open slots tool.
      - *schedulingConfig.timeRangeOverride* object used to limit the time range of the search for open slots tool.
        - *schedulingConfig.timeRangeOverride.startTime* is the start time to search for slots with.
        - *schedulingConfig.timeRangeOverride.endTime* is the end time to search for slots with.

- **Book Selected Slot**  
  Books a slot selected by the patient from the open slots search. To be used in conjunction with the Search For Open Slots tool.
  - Requires:
    - *ehrInformationId* is included in the metadata (included by default).
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  - Tool Arguments (provided by the assistant during the call):
    - *ehrTimeSlotId* is the ID of the slot to book.
  - Optional:
    - *schedulingConfig* can be included in the tools metadata to provide additional configuration for the book selected slot tool.
      - *schedulingConfig.bookAppointmentMockEnabled* boolean to enable mock data for the book selected slot tool.

- **Get Encounter Details**  
  Gets the details of an encounter stored in Notable.
  - Requires:
    - *ehrInformationId* is included in the metadata (included by default).
    - The patient to have been authenticated using the authenticateKnownPatient tool.
    - *encounterIds* is an array of encounter IDs to get the details of (needs to be included in the tools metadata).

- **Get Appointments**  
  Gets the appointments for a patient directly from FHIR or proprietary APIs.
  - Requires:
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  - Optional Tool Arguments (provided by the assistant during the call):
    - *startDate* is a date used to filter appointments that started after a greater or equal to this date. If not provided, it will default to NOW.
    - *endDate* is a date used to filter appointments that started after a greater or equal to this date.
    - *appointmentKinds* is an array of Notable Appointment Kinds used to filter the appointments by.
    - *providerEhrIds* is an array of provider EHR IDs used to filter the appointments by.
    - *status* is a string representing the FHIR appointment status used to filter the appointments by.

- **Get Patient**  
  Gets the details of a patient directly from FHIR or proprietary APIs.
  - Requires:
    - The patient to have been authenticated using the authenticateKnownPatient tool.

- **Get Encounters**
  Fetches encounters for a patient directly from the connected EHR via FHIR or proprietary APIs. This is a general tool call that can be used to get encounters for a patient within a specified date range.
  - Requires:
    - *ehrInformationId* is included in the metadata (included by default).
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  - Tool Arguments (provided by the assistant during the call):
    - *startTime* is the start time (ISO timestamp) to bound the encounter date range.
    - *endTime* is the end time (ISO timestamp) to bound the encounter date range. If not provided, the current date and time will be used.
    - *status* is an array of encounter statuses to filter by (e.g., scheduled, in-progress, completed, cancelled).
    - *type* is an array of encounter types to filter by (e.g., in-person, virtual, mobile).
  - Returns:
    - A list of encounters with the following properties:
      - *ehrReferenceValue* is the identifier by which this encounter is known. This is a string.
      - *encounterClass* is the classification of patient encounter (e.g., ambulatory, inpatient, emergency). This is a string.
      - *status* is the current status of the encounter (e.g., planned, arrived, in-progress, finished, cancelled). This is a string.
      - *types* is the specific type of encounter (e.g., e-mail consultation, surgical day-care). This is an array of strings.
      - *serviceType* is the broad categorization of the service provided (e.g., cardiology). This is a string.
      - *serviceProvider* is the organization primarily responsible for the encounter's services. This is a string.
      - *diagnoses* is the list of diagnosis relevant to this encounter. This is an array of objects with:
        - *condition* is the reason the encounter takes place (admission/discharge diagnosis). This is a string.
        - *role* is the role this diagnosis has within the encounter (e.g., admission, billing, discharge). This is a string or null.
        - *rank* is the ranking of the diagnosis for each role type. This is a number or null.
      - *appointmentIds* is the list of appointments that scheduled this encounter. This is an array of strings.
      - *startTime* is the start time of the encounter. This is a string.
      - *endTime* is the end time of the encounter. This is a string.
      - *length* is the quantity of time the encounter lasted. This is an object with:
        - *value* is the numeric value of the duration. This is a number.
        - *unit* is the unit of time (e.g., minutes, hours). This is a string.
      - *reasons* array of strings

- **Get Orders**
  Retrieves orders (service requests) for a patient directly from the connected EHR via FHIR or proprietary APIs. Orders represent requests for diagnostic tests, procedures, referrals, or other clinical services that have been ordered for the patient.
  - Requires:
    - *ehrInformationId* is included in the metadata (included by default).
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  - Required Tool Arguments (provided by the assistant during the call):
    - *authoredAfter* is the start date for order search in ISO 8601 format. The date range (authoredAfter to authoredBefore) cannot exceed 12 months.
  - Optional Tool Arguments (provided by the assistant during the call):
    - *authoredBefore* is the end date for order search in ISO 8601 format. If not provided, defaults to authoredAfter + 12 months.
    - *status* is used to filter orders by their current status. Valid values: 'active', 'completed', 'revoked'.
  - Returns:
    - A list of orders with the following properties:
      - *status* is the current status of the order (active, completed, revoked, draft, unknown, on-hold or entered-in-error). This is a string.
      - *procedure* is an object describing the requested procedure/service with:
        - *description* is a human-readable description of the procedure. This is a string.
        - *code* is the procedure code (CPT, LOINC, etc.). This is a string.
        - *codeSystem* is the coding system used for the procedure code. This is a string.
      - *provider* is an object with information about the ordering provider with:
        - *displayName* is the provider's display name. This is a string.
        - *ehrId* is the provider's EHR ID. This is a string.
      - *diagnoses* is the list of diagnoses associated with this order. This is an array of objects with:
        - *description* is a human-readable description of the diagnosis. This is a string.
        - *icd10Codes* is an array of ICD-10 codes for the diagnosis. This is an array of strings.
      - *created* is the date and time when the order was created. This is a string in ISO 8601 format.
      - *start* is the start date/time for when the order should be performed. This is a string or undefined.
      - *end* is the end date/time for when the order should be performed. This is a string or undefined.
      - *encounterId* is the EHR ID of the encounter associated with this order. This is a string or undefined.
      - *priority* is the priority of the order. This is a string or undefined.
      - *locations* is the list of locations associated with this order. This is undefined or an array of objects with:
        - *displayName* is the location's display name. This is a string or undefined.
        - *reference* is the location's reference. This is a string or undefined.
      - *orderDetails* is the list of details of the order. This is undefined or an array of strings.
      - *performers* is the list of performers associated with this order. This is undefined or an array of objects with:
        - *displayName* is the performer's display name. This is a string or undefined.
        - *reference* is the performer's reference. This is a string or undefined.
      - *patientInstruction* is the patient instruction for the order. This is a string or undefined.

- **Traverse Decision Tree**
  Traverses decision tree to iterate over questions in a prescriptive, deterministic manner. Optionally it can also export answers to specified questions to the EHR system.
  - Requires:
    - *decisionTreeId* is the ID of the decision tree to use, which you should create here: [Create Decision Tree](https://app.notablehealth.com/staff/app#/decision-trees).
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  
    When to use a decision tree: 
    - Do you need to export / save answers from the conversation into an EHR form?
      - Can we achieve this instead via post call transcript processing?
        - ie.Transcript → LLM block for data extraction → save extracted data to EHR form
        - If yes: not a hard requirement for using a decision tree
        - If no: maybe a good candidate for decision trees
    - Do you need to adhere to and always ask a set of questions?
      - If yes: have you tried prompt engineering for this use case first?
        - If prompt engineering did not work: it might be a good candidate for a decision tree.
      - If no: we’d recommend not using a decision tree for this use case to promote a more natural flow for your conversation

- **Get Practice Details**
  Gets the practice's business hours, address, and current open/closed status. Use this tool when the patient asks about practice hours, when the practice opens or closes, or needs the practice's contact information.
  - Requires:
    - The patient to have been authenticated using the authenticateKnownPatient tool.
  - Tool Arguments (provided by the assistant during the call):
    - *practiceId* is the ID of the practice to get details for. This is required.
  - **Important Configuration Note:**
    - When using this tool, you should provide a list of practice IDs in your prompt configuration. This allows the assistant to answer questions about the chosen practices.
    - Example prompt configuration:
      ~~~
      ##Guardrails
      When answering questions about practice details, only provide information about these practices:
      - Name of Practice A (ID: practice-id-1)
      - Name of Practice B (ID: practice-id-2)
      - Name of Practice C (ID: practice-id-3)
      ~~~
  - Returns:
    - *practiceId* is the practice's unique identifier. This is a string.
    - *practiceName* is the name of the practice. This is a string.
    - *formattedAddress* is the practice's full address formatted for display. This is a string.
    - *phoneNumber* is the practice's phone number. This is a string.
    - *isOpen* indicates whether the practice is currently open. This is a boolean.
    - *currentTime* is the current time in the practice's timezone (e.g., "January 20, 2026 at 3:45 PM EST"). This is a string.
    - *timezone* is the practice's timezone (e.g., "America/New_York"). This is a string.
    - *todaysHours* is an object describing today's hours with:
      - *summary* is a human-readable string of today's hours (e.g., "08:00 - 17:00" or "Closed"). This is a string.
      - *label* is an optional description if the practice is closed for a specific reason (e.g., "Holiday", "Office Closed"). This is a string or null.
    - *weeklySchedule* is an object with day names as keys and hours as values (e.g., \`{ "Monday": "08:00 - 17:00", "Saturday": "Closed" }\`). This is a Record<string, string>.
    - *upcomingClosures* is a list of upcoming closures in the next 14 days with descriptions (e.g., ["January 25 - Holiday"]). This is an array of strings.
    - *nextOpenTime* is when the practice will next be open if currently closed (e.g., "Monday at 08:00 AM"), or null if the practice is currently open. This is a string or null.

### **5. Set up Voice Call Transfer Destinations**
If you want the assistant to transfer the call to a different destination, you can do so by adding a voice call transfer destination. You can add up to 3 destinations.
- **Transfer call tool destinations**
  - **Transfer destination type**
    - Phone number
    - SIP URI
  - **Transfer destination**
    - Phone number to transfer the call to
    - SIP URI to transfer the call to
  - **Extension (optional for phone number destinations)**
    - Extension to transfer the call to
  - **Transfer mode**
    - _Blind Transfer_: The assistant will transfer the call to the destination without saying anything.
    - _Blind Transfer with Summary in SIP Header_: The assistant will transfer the call to the destination and add a SIP header X-Transfer-Summary to the call to include the summary. This is only available for SIP destinations.
    - _Warm Transfer With Message_: The assistant will transfer the call to the destination and say a message to the destination party before connecting the callee to the destination.
    - _Warm Transfer With Summary_: The assistant will transfer the call to the destination and provide a summary of the call to the destination party before connecting the callee to the destination.
    - _Warm Transfer With Message after Operator Speaks_: The assistant will transfer the call to the destination and wait for the operator to speak first before saying a message to the the destination party before connecting the callee to the destination.
    - _Warm Transfer With Summary after Operator Speaks_: The assistant will transfer the call to the destination and wait for the operator to speak first before providing a summary of the call to the destination party before connecting the callee to the destination.
  - **Description of the destination the call will be transferred to**
    - The description of the destination the call will be transferred to so the assistant knows where to transfer the call to.
    - Example: "Billing Department"
  - **Message assistant will speak to the callee before connecting them to the destination. Leave blank to not speak a message.**
    - The message the assistant will speak to the callee before connecting them to the destination.
    - Example: "I'm transferring you to the billing department. Please hold while I connect you."
  - **Message assistant will speak to the transfer destination before connecting callee**
    - The message the assistant will speak to the transfer destination before connecting the callee. This is only available for transfer modes with a message.
  - **Prompt used to generate the summary of the call using the call transcript.**
    - The prompt used to generate the summary of the call using the call transcript. This is only available for transfer modes with a summary.
    - Example: "Please provide a summary of the call using the call transcript."

## How to Test

To test this block:

1. Enter your phone number in the callee field.
2. Make sure any Test Patient you are starting the flow with has the same phone number (via Flow Tester).
3. Click 'Test'.
4. The flow will trigger a new run and you will receive a phone call from the Assistant.
5. Respond to the questions as if you are the patient and test different patient responses.
6. Review the output of the Voice Call block in the flow to see the transcript of the conversation.
7. Try various testing scenarios:
    - Patient confirms appointment
    - Patient needs to reschedule
    - Patient has additional questions
    - Patient authentication failures
    - Voicemail testing

## Best Practices

### Voice Persona Selection
When picking a voice persona, consider:
- **Target Audience:** Choose a dialect and accent that resonates with your primary patient demographic for relatability and comfort.
- **Neutrality:** For diverse audiences, consider using a neutral accent to ensure clear communication and avoid misunderstandings.
- **Brand Voice Alignment:** Select an accent that reflects your organization's identity and values.
- **Clarity and Comprehension:** Prioritize accents that allow for clear pronunciation and understanding.
- **Patient Demographics:** Consider the age, cultural background, and preferences of your patient population.
- **Use Case Appropriateness:** Match voice characteristics to the purpose of the call (e.g., empathetic for health concerns, professional for administrative matters).

Regularly review the voice and test with your organization and patients to ensure it remains appropriate and effective.

### Call Scheduling Considerations
- **Time Zones:** Schedule calls during appropriate hours in the patient's local time zone.
- **Respect Do-Not-Call Preferences:** Honor patient communication preferences and legal requirements.
- **Appropriate Frequency:** Avoid excessive calls to the same patient.
- **Call Windows:** Generally schedule between 8am-8pm local time on weekdays, 9am-6pm on weekends.
- **Healthcare-Specific Timing:** Consider medication schedules for adherence calls.
- **Retry Logic:** Set appropriate intervals between retry attempts (typically 3-4 hours).

### Concurrency and Rate Limiting
Voice Call blocks implement intelligent rate limiting to ensure system stability and optimal resource utilization. By default, a maximum of **10 concurrent voice calls** can be active at any given time. This throttling mechanism serves several important purposes:

- **Resource Management:** Prevents system overload during high-volume campaigns by queuing excess calls and processing them as capacity becomes available.
- **Graceful Degradation:** When the concurrency limit is reached, additional calls are queued rather than rejected, ensuring no patient outreach is lost.

**Note:** If your organization requires higher concurrency limits for large-scale outreach campaigns, contact your Notable representative to discuss custom throttling configurations.

### How to Write Effective Prompts

To enhance clarity and maintainability, break down system prompts into distinct sections:

- **Identity:** Define the persona and role of the AI assistant, setting the tone for interactions.
  \`\`\`
  ##Identity
  You are a practice coordinator calling the patient to help them prepare for an upcoming PCP visit. Be polite, calm, patient, and professional.
  \`\`\`

- **Style:** Establish stylistic guidelines, such as conciseness, formality, or humor, to ensure consistent communication.
  \`\`\`
  ##Style
  After all questions have been answered, ask if there is anything else you can help them with. Thank them for their time and say you looking forward to seeing the patient at the end of the call.
  \`\`\`

- **Guardrails:** Define boundaries and restrictions for the assistant.
  \`\`\`
  ##Guardrails
  Do not give them any information unless they explicitly ask for it. Give answers one at a time from the information in {{Practice_details.data}}.
  \`\`\`

- **Tasks & Goals:** Outline the assistant's objectives and the steps it should take to achieve them.
  \`\`\`
  ##Tasks and Goals
  Verification:
  Please verify the patient's identity before asking them any questions using their last name and date of birth from {{patient.data}}. Do not use the patient's name until they have been verified.
  \`\`\`

### Additional Resources
- [Prompt Engineering Guide](https://www.promptingguide.ai/)
- [Learn Prompting](https://learnprompting.org)
- [Open AI Prompt Engineering](https://platform.openai.com/docs/guides/prompt-engineering)
`;
